/**
 * Stage 38 — quantitative wording for the scenarios 6–7 anomaly reason codes.
 *
 * WHAT CHANGED AND WHY
 * Until now a reason code produced a sentence like «модель отметила сочетание импортного и
 * экспортного груза как редкое среди похожих связей». It named no number, no baseline and
 * no comparison group, so it could not be acted on — and «похожих связей» did not say what
 * counted as similar.
 *
 * The detail API now carries `anomalyExplanation`: for each code, the feature that actually
 * fired, its value for THIS case, the median and quartiles of that feature over the anomaly
 * model's own train population, the share of that population below/above the value, the
 * population size and the period it covers. This module turns those fields into Russian.
 *
 * THE ONE RULE
 * It FORMATS. It never derives, averages, extrapolates or fills in a missing figure, and it
 * never reads a score, percentile, SHAP contribution, rank or match score. A reason whose
 * shape it cannot phrase from confirmed fields produces NO card: the code stays in the
 * technical block and the analyst sees one honest sentence instead of a persuasive one.
 *
 * WHAT THE NUMBERS MEAN (frozen Stage 24 / Stage 32A semantics)
 *  • History counts are event counts in the normalised leg corpus for that family's key
 *    (cargo = ЕТСНГ, cargo+junction = ЕТСНГ + станция стыка, payer = код плательщика, …),
 *    over a window that ENDS THE DAY BEFORE this pair's own export date. They are not
 *    counts of candidate links.
 *  • `days_between` is this pair's own interval, in days.
 *  • The comparison population is the anomaly model's train split — rule-eligible
 *    cross-unit links of 2023–2024 — the same rows whose median and MAD define the
 *    threshold that raised the code.
 */

import type { Formatter } from '@/lib/i18n'
import {
  MAX_EVIDENCE_EXAMPLES,
  MIN_EVIDENCE_ROWS,
  pushEvidence,
  type EvidenceDrilldown,
  type EvidenceRow,
} from '../evidence-row'

/* ── API contract (mirrors app/candidate/reason_evidence.py) ──────────────── */

export interface ReasonEvidence {
  code: string
  feature: string
  subjectKind: string
  subjectKey: string | null
  measure: string
  windowDays: number | null
  observedValue: number | null
  baselineValue: number | null
  normalRangeMin: number | null
  normalRangeMax: number | null
  p10: number | null
  p90: number | null
  /** Share of the comparison population strictly below the observed value, 0…1. */
  shareBelow: number | null
  /** Share strictly above. */
  shareAbove: number | null
  zScore: number | null
  comparisonCount: number | null
  historicalPeriodStart: string | null
  historicalPeriodEnd: string | null
  evidenceSource: string | null
}

export interface AnomalyExplanationBlock {
  version: string
  evidenceStatus: 'complete' | 'partial' | 'unavailable' | string
  comparisonGroup: string | null
  comparisonCount: number | null
  historicalPeriodStart: string | null
  historicalPeriodEnd: string | null
  reasonRule?: string | null
  evidenceSource?: string | null
  reasons: ReasonEvidence[]
  reasonCodesWithoutEvidence: string[]
  identityStatus: 'exact' | 'ambiguous' | 'no_evidence' | string
}

/* ── vocabulary ───────────────────────────────────────────────────────────── */

/** Card title per reason code. Unknown codes get no card at all. */
export const REASON_TITLE: Record<string, string> = {
  UNUSUAL_CARGO_TRANSITION_HISTORY: 'Редкий груз на этом маршруте',
  UNUSUAL_TIMING: 'Необычный интервал',
  UNUSUAL_PARTICIPANT_HANDOFF: 'Необычная передача между участниками',
  UNUSUAL_PAYER_RELATIONSHIP: 'Необычная роль плательщика',
  UNUSUAL_JUNCTION_ACTIVITY: 'Необычная активность на стыке',
  UNUSUAL_UNIT_ACTIVITY_DIVERGENCE: 'Разная история транспортных единиц',
  UNUSUAL_CORRIDOR: 'Редкое направление',
  UNUSUAL_WEIGHT_RELATION: 'Необычное соотношение веса',
  UNSEEN_PRIOR_HISTORY: 'Нет истории за прошлый год',
  UNUSUAL_FEATURE_PROFILE: 'Необычный набор признаков',
}

/**
 * What one unit of the counted history is, per subject.
 *
 * `[nominative plural for a count, subject phrase]` — «119 перевозок груза с кодом ЕТСНГ
 * 542101». A subject with no entry produces no card: an unnamed count is not an
 * explanation.
 */
/**
 * ЧТО СЧИТАЕТСЯ ОДНОЙ ЕДИНИЦЕЙ (Stage 43 — проверено по корпусу, а не по названию поля)
 *
 * Строка корпуса `ev` — ОДНА НОГА перевозки: один вагон или контейнер под одним номером
 * отправки, на одной стороне. У кейса #05b2297d показатель `exp_participant_prev_90d`
 * равен 92 179, и за этим числом стоят 92 179 таких строк — но лишь 1 557 РАЗНЫХ номеров
 * отправок. Слово «отправок» в подписи расходилось с величиной в 59 раз, поэтому счётные
 * существительные здесь — «записи», а `of` называет, записи ЧЕГО это.
 *
 * СТОРОНА. Семейство ключуется одной колонкой `ev` и считает ВСЕ ноги с этим ключом, с
 * обеих сторон: `participant_bin` — это `receiver_bin` у импортной ноги и `sender_bin` у
 * экспортной (24_cross_unit_feat_eng.ipynb). Значит `exp_participant_prev_90d`, который
 * пробуется собственным `export_sender_bin` пары, считает этот БИН и как получателя
 * приходящих импортов, и как отправителя уходящих экспортов. Прежний текст «Участник
 * экспортной отправки ОТПРАВЛЯЛ грузы» приписывал числу односторонний смысл, которого у
 * него нет, — и именно поэтому в примерах законно стояли импортные строки.
 *
 * `sides` печатается в самом тексте причины. Он ничего не фильтрует: отбор одной стороны
 * дал бы набор, размер которого не равен числу, отданному модели.
 */
const SUBJECT_COUNT: Record<string, {
  noun: [string, string, string]
  of: string
  clause: (dir: string) => string
  /** Какие ноги входят в счёт, словами. `null` — сторона в смысле показателя не участвует. */
  sides?: string | null
}> = {
  cargo_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'о перевозке груза с этим кодом ЕТСНГ',
    clause: (dir) => `Груз с этим кодом ЕТСНГ перевозился заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги',
  },
  cargo_junction_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'о перевозке этого груза через этот стык',
    clause: (dir) => `Этот груз через этот стык перевозился заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги',
  },
  junction_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'о перевозке через этот стык',
    clause: (dir) => `Через этот стык перевозки шли заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'прибытия импортных ног и отправления экспортных',
  },
  corridor_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'о перевозке с этой страной на дальнем конце',
    clause: (dir) => `По этому направлению перевозки шли заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'страна отправления импортных ног и страна назначения экспортных',
  },
  import_unit_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'об импортной транспортной единице',
    clause: (dir) => `Импортная транспортная единица встречалась в перевозках заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги этой единицы',
  },
  export_unit_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'об экспортной транспортной единице',
    clause: (dir) => `Экспортная транспортная единица встречалась в перевозках заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги этой единицы',
  },
  unit_pair_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'об этой паре транспортных единиц вместе',
    clause: (dir) => `Эта пара транспортных единиц встречалась в перевозках вместе заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги обеих единиц',
  },
  import_participant_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'с участником импортной отправки',
    clause: (dir) =>
      `Участник импортной отправки встречался в перевозках заметно ${dir} обычного уровня `
      + 'для сопоставимых связей — как получатель импортных отправок и как отправитель экспортных.',
    sides: 'получатель импортных ног и отправитель экспортных',
  },
  export_participant_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'с участником экспортной отправки',
    clause: (dir) =>
      `Участник экспортной отправки встречался в перевозках заметно ${dir} обычного уровня `
      + 'для сопоставимых связей — как отправитель экспортных отправок и как получатель импортных.',
    sides: 'отправитель экспортных ног и получатель импортных',
  },
  participant_pair_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'с этой парой участников',
    clause: (dir) => `Эта пара участников встречалась заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги обоих участников',
  },
  payer_history: {
    noun: ['запись', 'записи', 'записей'],
    of: 'с этим кодом плательщика',
    clause: (dir) => `Этот плательщик оплачивал перевозки заметно ${dir} обычного уровня для сопоставимых связей.`,
    sides: 'импортные и экспортные ноги',
  },
}

/* ── subject identity (Stage 42) ──────────────────────────────────────────── */

/**
 * Кто именно стоит за причиной.
 *
 * Причина по плательщику печаталась как «Этот плательщик оплачивал отправки…»,
 * не называя ни кода, ни стороны, — при том что признак `payer_prev_30d`
 * ключуется строго на КОД ПЛАТЕЛЬЩИКА ИМПОРТНОЙ ноги (Stage 24,
 * `build_history("payer", "payer_code", "import_payer_code")`; тем же ключом
 * отбираются строки drill-down в `evidence_examples.FAMILY_KEY`). Без
 * идентификатора фраза «этот плательщик» приписывает кейсу активность, которую
 * с ним нельзя связать однозначно.
 *
 * Модуль ничего не вычисляет: идентификатор приходит из карточки, где он
 * прочитан из полей ноги.
 */
export interface ReasonSubjectContext {
  payer?: {
    /** Сторона, по которой ключуется признак. */
    side: 'import' | 'export'
    /** Код, однозначно идентифицирующий плательщика; `null` у заполнителя. */
    code: string | null
    name: string | null
    identified: boolean
    /** Код источника как есть — для технической строки. */
    rawCode: string | null
  }
  /**
   * Участник, которым ключуется семейство `imp_participant` / `exp_participant`.
   *
   * Stage 43. Причина называлась «участник экспортной отправки», но НЕ называла, кто это,
   * — и проверить, к тому ли лицу относятся 92 179 записей, было нечем. БИН приходит из
   * той ноги карточки, которой Stage 24 пробует семейство (`export_sender_bin` для
   * `exp_participant`, `import_receiver_bin` для `imp_participant`), название — из той же
   * ноги, из соседнего поля. Ничего не ищется по названию и не берётся у плательщика:
   * участник и плательщик — разные поля источника и в этом кейсе разные организации.
   */
  participant?: {
    /** Нога карточки, из которой прочитан ключ. */
    side: 'import' | 'export'
    /** Роль участника на ЭТОЙ ноге — та, которой он в неё попал. */
    role: 'sender' | 'receiver'
    /** БИН; `null` — источник его не несёт, и утверждать что-либо об участнике нельзя. */
    bin: string | null
    name: string | null
    identified: boolean
  }
}

const PAYER_SIDE_TEXT: Record<'import' | 'export', string> = {
  import: 'импортной отправки',
  export: 'экспортной отправки',
}

const PARTICIPANT_SIDE_TEXT: Record<'import' | 'export', string> = {
  import: 'импортной отправки',
  export: 'экспортной отправки',
}

/** Роль участника на ноге, из которой прочитан ключ. */
const PARTICIPANT_ROLE_TEXT: Record<'sender' | 'receiver', string> = {
  sender: 'отправитель',
  receiver: 'получатель',
}

/**
 * Как одно и то же лицо попадает в счёт с каждой стороны.
 *
 * Одна колонка `participant_bin` несёт получателя у импортной ноги и отправителя у
 * экспортной, поэтому один БИН законно стоит в строках обеих сторон — в двух разных
 * ролях. Без этой строки импортная строка под причиной «участник экспортной отправки»
 * читается как чужая.
 */
export const PARTICIPANT_ROLE_BY_SIDE_TEXT =
  'получатель в импортных записях, отправитель в экспортных'

/** Fail-closed: у участника нет БИН — про участника не утверждается ничего. */
export const PARTICIPANT_NOT_IDENTIFIED_TEXT =
  'Участник не установлен: в источнике нет его БИН. Историческую активность невозможно '
  + 'однозначно связать с текущим кейсом.'

/** «ТОО «Богатырь Комир» · БИН 970340000843» / «БИН 970340000843». */
export function participantText(p: NonNullable<ReasonSubjectContext['participant']>): string | null {
  if (!p.bin) return p.name ? `${p.name} · БИН не указан` : null
  return p.name ? `${p.name} · БИН ${p.bin}` : `БИН ${p.bin}`
}

/** Fail-closed текст причины по плательщику, которого источник не установил. */
export const PAYER_NOT_IDENTIFIED_TEXT =
  'Плательщик не установлен. Историческую активность невозможно однозначно связать с текущим кейсом.'

/** Subject of a «дней с прошлого события» measure. */
const SUBJECT_SINCE: Record<string, string> = {
  cargo_history: 'прошлой перевозки груза с этим кодом ЕТСНГ',
  cargo_junction_history: 'прошлой перевозки этого груза через этот стык',
  junction_history: 'прошлой перевозки через этот стык',
  corridor_history: 'прошлой перевозки по этому направлению',
  import_unit_history: 'прошлой отправки импортной транспортной единицы',
  export_unit_history: 'прошлой отправки экспортной транспортной единицы',
  unit_history: 'прошлой отправки транспортной единицы',
  import_participant_history: 'прошлой отправки участника импортной отправки',
  export_participant_history: 'прошлой отправки участника экспортной отправки',
  payer_history: 'прошлой отправки этого плательщика',
}

const DAYS: [string, string, string] = ['день', 'дня', 'дней']

/* ── number formatting ────────────────────────────────────────────────────── */

function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(Math.trunc(n))
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}

/** «1 054 251» — ru grouping, no fake decimals on a count. */
export function num(n: number, f: Formatter): string {
  return Number.isInteger(n) ? f.number(n) : f.number(n, { maximumFractionDigits: 2 })
}

function count(n: number, forms: [string, string, string], f: Formatter): string {
  return `${num(n, f)} ${pluralRu(n, forms)}`
}

/**
 * A share of the population as a percentage.
 *
 * Both ends are capped rather than rounded to 100.0% / 0.0%: a share of 0.99995 does not
 * show that the value is beyond EVERY row in the population, and the capped figure is the
 * statement the data supports.
 */
export function sharePct(share: number): string {
  const pct = Math.min(99.9, Math.max(0.1, share * 100))
  return `${pct.toFixed(1)}%`
}

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

/** «2023–2024» from the two ISO period bounds, or null when either is missing. */
export function periodYears(start: string | null | undefined, end: string | null | undefined): string | null {
  const a = /^(\d{4})/.exec(String(start ?? ''))?.[1]
  const b = /^(\d{4})/.exec(String(end ?? ''))?.[1]
  if (!a || !b) return null
  return a === b ? a : `${a}–${b}`
}

/** «1 054 251 связи 2023–2024 годов» — the comparison group, named, never «похожих связей». */
export function comparisonPhrase(e: { comparisonCount: number | null; historicalPeriodStart: string | null; historicalPeriodEnd: string | null }, f: Formatter): string | null {
  if (!isNum(e.comparisonCount)) return null
  const base = `${count(e.comparisonCount, ['связь', 'связи', 'связей'], f)} сценариев 6–7`
  const years = periodYears(e.historicalPeriodStart, e.historicalPeriodEnd)
  return years ? `${base} за ${years} годы` : base
}

/* ── the sentence ─────────────────────────────────────────────────────────── */

export interface RenderedEvidenceReason {
  code: string
  title: string
  /**
   * ONE short conclusion. Stage 39: the numbers moved into `evidence` below it — the
   * sentence names at most the case's own observed value, never the whole comparison.
   */
  text: string
  /** The comparison, as a two-column table (same component the scenarios 1–5 drawer uses). */
  evidence: EvidenceRow[]
  /** One line under the table; window semantics only. */
  note?: string | null
  /** Short form for the analyst checklist — the same numbers, imperative mood. */
  checklistText: string
}

/** Семейства, у которых субъект — участник (БИН), а не груз, стык или единица. */
const PARTICIPANT_SUBJECT_KINDS = new Set(['import_participant_history', 'export_participant_history'])

/** Shown in place of the table when a reason has fewer than two supported numbers. */
export const INSUFFICIENT_EVIDENCE_TEXT =
  'В текущем снимке недостаточно числовых данных для точного объяснения этой причины.'

/** «1 054 251 сопоставимая связь» — the reference population is LINKS, never shipments. */
function comparisonVolume(e: ReasonEvidence, f: Formatter): string | null {
  return isNum(e.comparisonCount)
    ? `${num(e.comparisonCount, f)} ${pluralRu(e.comparisonCount, ['сопоставимая связь', 'сопоставимые связи', 'сопоставимых связей'])}`
    : null
}

/** «реже, чем у 99.7%» — position against the population, direction from the value. */
function positionValue(e: ReasonEvidence, higher: string, lower: string): string | null {
  if (!isNum(e.observedValue) || !isNum(e.baselineValue)) return null
  if (e.observedValue > e.baselineValue && isNum(e.shareBelow)) return `${higher}, чем у ${sharePct(e.shareBelow)}`
  if (e.observedValue < e.baselineValue && isNum(e.shareAbove)) return `${lower}, чем у ${sharePct(e.shareAbove)}`
  return null
}

/**
 * «331 310 — 354 982» — the interquartile range, stated as a range only when the two
 * quartiles actually differ. Otherwise the median is the honest single value.
 */
function rangeValue(f: Formatter, e: ReasonEvidence, forms?: [string, string, string]): { label: string; value: string } | null {
  const unit = (v: number) => (forms ? count(v, forms, f) : num(v, f))
  if (isNum(e.normalRangeMin) && isNum(e.normalRangeMax) && e.normalRangeMin !== e.normalRangeMax) {
    // «331 310 — 354 982 перевозок»: in a range the noun is stated once, in the genitive
    // plural, and is NOT declined against the upper bound.
    return {
      label: 'Диапазон у центральных 50% связей',
      value: `${num(e.normalRangeMin, f)} — ${num(e.normalRangeMax, f)}${forms ? ` ${forms[2]}` : ''}`,
    }
  }
  if (isNum(e.baselineValue)) return { label: 'Обычное значение', value: unit(e.baselineValue) }
  return null
}

/**
 * «Это дольше, чем у 97.8% сопоставимых связей» — direction taken from the value itself,
 * never assumed. Returns null when the value sits exactly at the median or a share is
 * missing, because then there is nothing to compare.
 */
function comparisonClause(e: ReasonEvidence, higher: string, lower: string): string | null {
  if (!isNum(e.observedValue) || !isNum(e.baselineValue)) return null
  if (e.observedValue > e.baselineValue && isNum(e.shareBelow)) {
    return `Это ${higher}, чем у ${sharePct(e.shareBelow)} сопоставимых связей`
  }
  if (e.observedValue < e.baselineValue && isNum(e.shareAbove)) {
    return `Это ${lower}, чем у ${sharePct(e.shareAbove)} сопоставимых связей`
  }
  return null
}

/**
 * «у половины из них — от 0 до 3 дней» (interquartile range) or «обычное значение — 1 день»
 * (median) — the range when the quartiles differ, the median otherwise.
 */
function usualClause(f: Formatter, e: ReasonEvidence, forms?: [string, string, string], asRange = true): string | null {
  const unit = (v: number) => (forms ? count(v, forms, f) : num(v, f))
  if (asRange && isNum(e.normalRangeMin) && isNum(e.normalRangeMax) && e.normalRangeMin !== e.normalRangeMax) {
    // «от 0 до 3 дней» — in this Russian construction the noun is stated once, in the
    // genitive plural, and is NOT declined against the upper bound («до 3 дня» is wrong).
    const noun = forms ? ` ${forms[2]}` : ''
    return `у половины из них — от ${num(e.normalRangeMin, f)} до ${num(e.normalRangeMax, f)}${noun}`
  }
  if (isNum(e.baselineValue)) return `обычное значение — ${unit(e.baselineValue)}`
  return null
}

/** Assembles «<observation>. <comparison>: <usual>. Сравнение: <population>.» */
function sentence(observation: string, comparison: string | null, usual: string | null, tail: string): string {
  const middle = comparison && usual ? `${comparison}: ${usual}.` : comparison ? `${comparison}.` : usual
    ? `Для сравнения: ${usual}.`
    : ''
  return [`${observation}.`, middle, tail].filter(Boolean).join(' ').trim()
}

/**
 * One reason card, or null when the evidence cannot be phrased from confirmed fields.
 *
 * Returning null is a feature: Stage 38 forbids a generic sentence standing in for a number
 * that is not there.
 */
export function renderEvidenceReason(
  f: Formatter,
  e: ReasonEvidence,
  subjects?: ReasonSubjectContext,
): RenderedEvidenceReason | null {
  const title = REASON_TITLE[e.code]
  if (!title || !isNum(e.observedValue)) return null
  const rows: EvidenceRow[] = []
  const volume = comparisonVolume(e, f)
  const period = periodYears(e.historicalPeriodStart, e.historicalPeriodEnd)
  const periodText = period ? `${period} годы` : null

  const finish = (text: string, checklistText: string): RenderedEvidenceReason => {
    pushEvidence(rows, 'Объём сравнения', volume)
    pushEvidence(rows, 'Период сравнения', periodText)
    // Two supported numbers or none: a lone value with no comparison is not evidence.
    if (rows.length < MIN_EVIDENCE_ROWS) {
      return { code: e.code, title, text: INSUFFICIENT_EVIDENCE_TEXT, evidence: [], checklistText }
    }
    return { code: e.code, title, text, evidence: rows, checklistText }
  }

  // this pair's own interval
  if (e.subjectKind === 'pair_interval' && e.measure === 'days_between_legs') {
    const observed = count(e.observedValue, DAYS, f)
    const longer = e.observedValue > (e.baselineValue ?? 0)
    pushEvidence(rows, 'Интервал этого кейса', observed)
    const range = rangeValue(f, e, DAYS)
    if (range) pushEvidence(rows, range.label, range.value)
    pushEvidence(rows, 'Положение относительно истории', positionValue(e, 'дольше', 'короче'))
    const usualShort = isNum(e.baselineValue) ? count(e.baselineValue, DAYS, f) : null
    return finish(
      `Между отправками прошло ${observed} — заметно ${longer ? 'больше' : 'меньше'} обычного интервала для сопоставимых связей.`,
      `Проверить интервал ${observed}${range ? `, обычно ${range.value}` : usualShort ? `, обычно ${usualShort}` : ''}`,
    )
  }

  // history event counts
  if (e.measure === 'events_in_window' || e.measure === 'events_total' || e.measure === 'events_prior_year') {
    const s = SUBJECT_COUNT[e.subjectKind]
    if (!s) return null
    const window =
      e.measure === 'events_in_window' && isNum(e.windowDays)
        ? `за ${count(e.windowDays, DAYS, f)} до этой перевозки`
        : e.measure === 'events_prior_year'
          ? 'за прошлый календарный год'
          : 'за всю историю'
    const observed = `${count(e.observedValue, s.noun, f)} ${window}`
    const rarer = e.observedValue < (e.baselineValue ?? 0)
    const payer = e.subjectKind === 'payer_history' ? subjects?.payer : undefined
    const participant = PARTICIPANT_SUBJECT_KINDS.has(e.subjectKind) ? subjects?.participant : undefined
    // Stage 40: this row IS a count over a concrete set of corpus events, so it can be
    // opened. The baseline, the position and the population size below cannot — they are
    // statistics of a distribution, not row sets.
    pushEvidence(rows, 'В этом кейсе', observed, { drilldown: windowDrilldown(e, s) })
    // Stage 43 — ЧТО именно посчитано. Без этой строки «92 179 записей» читается как
    // 92 179 отправок, а за ними стоит 1 557 номеров отправок: одна отправка едет на
    // многих вагонах, и каждая её нога — отдельная запись корпуса.
    pushEvidence(rows, 'Единица счёта',
      `нога перевозки: один вагон или контейнер в одной отправке, ${s.of}`)
    if (s.sides) pushEvidence(rows, 'Что входит в счёт', s.sides)
    const range = rangeValue(f, e, s.noun)
    if (range) pushEvidence(rows, range.label, range.value)
    pushEvidence(rows, 'Положение относительно истории', positionValue(e, 'чаще', 'реже'))
    // Stage 43 — чей это показатель. Та же логика, что у плательщика ниже: без
    // идентификатора «участник экспортной отправки» проверить нельзя.
    if (participant) {
      pushEvidence(rows, 'Участник', participantText(participant), {
        tooltip: participant.identified
          ? 'БИН прочитан из ноги карточки, которой признак пробует историю; это НЕ плательщик.'
          : 'Источник не несёт БИН участника: связать историю с этим кейсом однозначно нельзя.',
      })
      pushEvidence(rows, 'Сторона расчёта',
        `${PARTICIPANT_ROLE_TEXT[participant.role]} ${PARTICIPANT_SIDE_TEXT[participant.side]}`)
      pushEvidence(rows, 'Роль в исторических записях', PARTICIPANT_ROLE_BY_SIDE_TEXT)
      if (isNum(e.windowDays)) {
        pushEvidence(rows, 'Период подсчёта', `${count(e.windowDays, DAYS, f)} до даты экспортной отправки`)
      }
    }
    // Stage 42 — чей это показатель. Строки стоят под таблицей причины, а не в
    // общем техническом блоке: без них «этот плательщик» не проверить.
    if (payer) {
      pushEvidence(rows, 'Использованный код плательщика', payer.identified ? payer.code : payer.rawCode, {
        tooltip: payer.identified
          ? null
          : 'Код из источника не идентифицирует плательщика: по нему собраны все отправки без названного плательщика.',
      })
      pushEvidence(rows, 'Сторона расчёта', `плательщик ${PAYER_SIDE_TEXT[payer.side]}`)
      if (isNum(e.windowDays)) {
        pushEvidence(rows, 'Период подсчёта', `${count(e.windowDays, DAYS, f)} до даты экспортной отправки`)
      }
    }
    if (participant && !participant.identified) {
      // Fail-closed: числа остаются, утверждение «этот участник» — нет.
      return finish(
        PARTICIPANT_NOT_IDENTIFIED_TEXT,
        `Установить участника ${PARTICIPANT_SIDE_TEXT[participant.side]}: `
        + `в источнике нет БИН, а по ключу насчитано ${observed}`,
      )
    }
    if (payer && !payer.identified) {
      // Fail-closed: числа остаются, утверждение о «этом плательщике» — нет.
      return finish(
        PAYER_NOT_IDENTIFIED_TEXT,
        `Установить плательщика ${PAYER_SIDE_TEXT[payer.side]}: `
        + `в источнике стоит код ${payer.rawCode ?? '—'}, по которому насчитано ${observed}`,
      )
    }
    const clause = payer
      ? `Плательщик ${PAYER_SIDE_TEXT[payer.side]}${payer.name ? ` «${payer.name}»` : ''} `
        + `(код ${payer.code}) оплачивал перевозки заметно ${rarer ? 'реже' : 'чаще'} `
        + 'обычного уровня для сопоставимых связей.'
      : participant
        // Тот же смысл, что у s.clause, но с названным участником: «кто» из общей фразы
        // не выводится, а стоит в ней явно — и сторона счёта названа целиком.
        ? `Участник ${PARTICIPANT_SIDE_TEXT[participant.side]}`
          + `${participant.name ? ` «${participant.name}»` : ''} (БИН ${participant.bin}) `
          + `встречался в перевозках заметно ${rarer ? 'реже' : 'чаще'} обычного уровня для `
          + `сопоставимых связей — ${PARTICIPANT_ROLE_BY_SIDE_TEXT}.`
        : s.clause(rarer ? 'реже' : 'чаще')
    return finish(
      clause,
      `Проверить основание: ${observed}${range ? `, обычно ${range.value}` : ''}`,
    )
  }

  // days since the previous event of the same key
  if (e.measure === 'days_since_previous_event') {
    const subject = SUBJECT_SINCE[e.subjectKind]
    if (!subject) return null
    const observed = count(e.observedValue, DAYS, f)
    const longer = e.observedValue > (e.baselineValue ?? 0)
    pushEvidence(rows, 'В этом кейсе', observed)
    const range = rangeValue(f, e, DAYS)
    if (range) pushEvidence(rows, range.label, range.value)
    pushEvidence(rows, 'Положение относительно истории', positionValue(e, 'дольше', 'короче'))
    return finish(
      `С ${subject} прошло ${observed} — заметно ${longer ? 'дольше' : 'меньше'} обычного для сопоставимых связей.`,
      `Проверить перерыв в ${observed} с ${subject}`,
    )
  }

  // weight relation between the two legs
  if (e.subjectKind === 'weight_relation' && e.measure === 'weight_difference_pct') {
    const observed = `${e.observedValue.toFixed(1)}%`
    const bigger = e.observedValue > (e.baselineValue ?? 0)
    pushEvidence(rows, 'Разница веса в этом кейсе', observed)
    if (isNum(e.baselineValue)) pushEvidence(rows, 'Обычное значение', `${e.baselineValue.toFixed(1)}%`)
    pushEvidence(rows, 'Положение относительно истории', positionValue(e, 'больше', 'меньше'))
    return finish(
      `Разница веса между отправками заметно ${bigger ? 'больше' : 'меньше'} обычной для сопоставимых связей.`,
      `Сверить разницу веса ${observed}`,
    )
  }

  // Anything else (rarity indices, burst ratios, log ratios): the value is real but there
  // is no phrasing that would mean something to an analyst, so no card is produced.
  return null
}

/**
 * Drill-down descriptor for a window count, or null when the number is not a row count.
 *
 * `totalCount` is the observed value itself — the aggregate and the set size are the same
 * quantity, which is what makes «50 из 119» honest. The endpoint is named here so the
 * component never assembles a URL of its own.
 */
function windowDrilldown(e: ReasonEvidence, s?: { sides?: string | null }): EvidenceDrilldown | null {
  if (!isNum(e.observedValue) || e.observedValue <= 0) return null
  if (e.measure !== 'events_in_window') return null
  const window = isNum(e.windowDays) ? `${e.windowDays} дней` : 'окно'
  return {
    evidenceKey: e.code,
    datasetType: e.subjectKind,
    totalCount: Math.trunc(e.observedValue),
    maxDisplayedItems: MAX_EVIDENCE_EXAMPLES,
    // «Перевозки» обещало по строке на перевозку; строка — одна её нога.
    title: 'Записи корпуса, вошедшие в этот показатель',
    endpoint: `candidate-pair-evidence/${e.code}`,
    // Сторона называется здесь же: заголовок причины говорит «экспортной», а набор — обе
    // стороны, и читатель должен узнать это до того, как откроет список.
    selectionDescription:
      `ноги корпуса за ${window} до даты экспортной отправки, от новых к старым`
      + (s?.sides ? ` (${s.sides})` : ''),
  }
}

/** Every reason that can be stated quantitatively, in the API's own order. */
export function renderEvidenceReasons(
  f: Formatter,
  block: AnomalyExplanationBlock | null | undefined,
  subjects?: ReasonSubjectContext,
): RenderedEvidenceReason[] {
  if (!block || block.identityStatus === 'ambiguous') return []
  return (block.reasons ?? [])
    .map((r) => renderEvidenceReason(f, r, subjects))
    .filter((r): r is RenderedEvidenceReason => r !== null)
}

/** Shown when the snapshot carries no usable numbers for any code. */
export const NO_EVIDENCE_TEXT =
  'Для этого кейса в текущем снимке не сохранены числовые данные, необходимые для точного ' +
  'объяснения причины нетипичности.'
